import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Wrapper, Status } from "@googlemaps/react-wrapper";
import { api } from "../../utils/api";

import "./GoogleMap.css";

const MapComponent = ({ center, zoom, polygons, setPolygons }) => {
  const ref = useRef(null);
  const [map, setMap] = useState(null);
  const [drawingManager, setDrawingManager] = useState(null);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [userPolygons, setUserPolygons] = useState([]);
  const staticPolygonRef = React.useRef([]); // will hold API polygon objects without causing rerenders

  const globalInfoWindow = new window.google.maps.InfoWindow();

  useEffect(() => {
    if (ref.current && !map) {
      const newMap = new window.google.maps.Map(ref.current, {
        center,
        zoom,
        styles: [
          {
            featureType: "all",
            elementType: "labels.text",
            stylers: [{ visibility: "on" }],
          },
          {
            featureType: "landscape",
            elementType: "all",
            stylers: [{ color: "#f2f2f2" }],
          },
          {
            featureType: "landscape",
            elementType: "geometry.fill",
            stylers: [{ color: "#ffffff" }],
          },
          {
            featureType: "landscape.man_made",
            elementType: "geometry.fill",
            stylers: [{ color: "#ffffff" }],
          },
          {
            featureType: "poi",
            elementType: "all",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "road",
            elementType: "all",
            stylers: [{ saturation: -100 }, { lightness: 45 }],
          },
          {
            featureType: "road",
            elementType: "geometry.fill",
            stylers: [{ color: "#eeeeee" }],
          },
          {
            featureType: "road",
            elementType: "labels.text.fill",
            stylers: [{ color: "#7b7b7b" }],
          },
          {
            featureType: "road",
            elementType: "labels.text.stroke",
            stylers: [{ color: "#ffffff" }],
          },
          {
            featureType: "road.highway",
            elementType: "all",
            stylers: [{ visibility: "simplified" }],
          },
          {
            featureType: "road.arterial",
            elementType: "labels.icon",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "transit",
            elementType: "all",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "water",
            elementType: "all",
            stylers: [{ color: "#46bcec" }, { visibility: "on" }],
          },
          {
            featureType: "water",
            elementType: "geometry.fill",
            stylers: [{ color: "#c8d7d4" }],
          },
          {
            featureType: "water",
            elementType: "labels.text.fill",
            stylers: [{ color: "#070707" }],
          },
          {
            featureType: "water",
            elementType: "labels.text.stroke",
            stylers: [{ color: "#ffffff" }],
          },
        ],
      });

      setMap(newMap);

      // Draw polygons from props (API data)
      if (Array.isArray(polygons)) {
        const polygonObjs = polygons
          .map((item, idx) => {
            if (!item.polygon || !Array.isArray(item.polygon)) return null;
            const polygon = new window.google.maps.Polygon({
              paths: item.polygon,
              fillColor: "#22c55e", // green for active
              fillOpacity: 0.3,
              strokeColor: "#16a34a",
              strokeWeight: 2,
              clickable: true,
              editable: false,
              draggable: false,
            });
            polygon.setMap(newMap);

            // Attach the id to the polygon object for later reference
            polygon.id = item.id || item.property_no || idx;

            return polygon;
          })
          .filter(Boolean);
        staticPolygonRef.current = polygonObjs;
      }

      // Initialize Drawing Manager only if available
      if (window.google?.maps?.drawing?.DrawingManager) {
        const drawingManagerInstance =
          new window.google.maps.drawing.DrawingManager({
            drawingMode: null,
            drawingControl: false,
            drawingControlOptions: {
              position: window.google.maps.ControlPosition.TOP_CENTER,
              drawingModes: [window.google.maps.drawing.OverlayType.POLYGON],
            },
            polygonOptions: {
              fillColor: "#FF0000",
              fillOpacity: 0.2,
              strokeColor: "#FF0000",
              strokeWeight: 2,
              clickable: true,
              editable: true,
              draggable: true,
            },
          });

        drawingManagerInstance.setMap(newMap);
        setDrawingManager(drawingManagerInstance);

        // Listen for polygon completion
        drawingManagerInstance.addListener(
          "polygoncomplete",
          async (polygon) => {
            const newPolygon = {
              id: Date.now(),
              polygon: polygon,
              name: `Custom Area ${userPolygons.length + 1}`,
              status: "Custom",
              color: "#FF0000",
            };

            setUserPolygons((prev) => [...prev, newPolygon]);

            // Log initial coordinates of the completed user polygon
            const path = polygon.getPath();
            const getCoordsFromPath = () => {
              const coords = [];
              for (let i = 0; i < path.getLength(); i += 1) {
                const point = path.getAt(i);
                coords.push({ lat: point.lat(), lng: point.lng() });
              }
              return coords;
            };
            const coordinates = getCoordsFromPath();
            console.log("User polygon created:", newPolygon.name, coordinates);

            // Automatically derive address metadata from map (no user input)
            const computeCentroid = (coords) => {
              let lat = 0;
              let lng = 0;
              coords.forEach((c) => {
                lat += c.lat;
                lng += c.lng;
              });
              return { lat: lat / coords.length, lng: lng / coords.length };
            };

            const centroid = computeCentroid(coordinates);

            const geocoder = new window.google.maps.Geocoder();

            const buildAndAttachMeta = (
              property_no,
              address_line,
              city,
              status
            ) => {
              const outputObject = {
                property_no: property_no || "",
                address_line: address_line || "",
                city: city || "",
                polygon: coordinates,
                status: status || "active",
              };

              polygon.__meta = outputObject;

              return outputObject;
            };

            // Try reverse geocoding the centroid to get address components

            geocoder.geocode(
              { location: centroid },
              async (results, statusGeo) => {
                let property_no = "";
                let address_line = "";
                let city = "";
                let statusValue = "active";
                if (statusGeo === "OK" && results && results[0]) {
                  const result = results[0];
                  const components = result.address_components || [];
                  const find = (type) => {
                    const comp = components.find((c) => c.types.includes(type));
                    return comp ? comp.long_name : null;
                  };
                  const street_number = find("street_number");
                  const route = find("route");
                  const subpremise = find("subpremise");
                  const locality =
                    find("locality") ||
                    find("postal_town") ||
                    find("administrative_area_level_2");
                  address_line = [route, street_number]
                    .filter(Boolean)
                    .join(" ");
                  property_no =
                    subpremise || street_number || result.place_id || "";
                  city = locality || "";
                }
                // fallback values already set above
                const outputObject = buildAndAttachMeta(
                  property_no,
                  address_line,
                  city,
                  statusValue
                );
                // Send to /api/properties
                try {
                  const res = await api.postProperty({
                    property_no: outputObject.property_no,
                    address_line: outputObject.address_line,
                    city: outputObject.city,
                    polygon: outputObject.polygon,
                    status: outputObject.status,
                  });
                  if (res && res.message) {
                    toast.success(res.message);
                  } else {
                    toast.success("Property created successfully");
                  }
                  console.log("Polygon property posted to /api/properties");
                } catch (error) {
                  toast.error("Failed to create property");
                  console.error(
                    "Failed to POST property to /api/properties:",
                    error
                  );
                }
              }
            );

            // Removed call to api.savePolygon (no longer used)

            // Log coordinates on edits to the user polygon
            path.addListener("set_at", () => {
              console.log(
                "User polygon updated (set_at):",
                newPolygon.name,
                getCoordsFromPath()
              );
            });
            path.addListener("insert_at", () => {
              console.log(
                "User polygon updated (insert_at):",
                newPolygon.name,
                getCoordsFromPath()
              );
            });
            path.addListener("remove_at", () => {
              console.log(
                "User polygon updated (remove_at):",
                newPolygon.name,
                getCoordsFromPath()
              );
            });

            // Add info window for user polygon
            const infoWindow = new window.google.maps.InfoWindow({
              content: `
              <div class="map-info-window">
                <h3>${newPolygon.name}</h3>
                <p><strong>Type:</strong> Custom Area</p>
                <p><strong>Status:</strong> User Created</p>
              </div>
            `,
            });
            polygon.infoWindow = infoWindow; // Store reference
            polygon.addListener("click", () => {
              globalInfoWindow.setContent(`
                <div class="map-info-window">
                  <h3>${newPolygon.name}</h3>
                  <p><strong>Type:</strong> Custom Area</p>
                  <p><strong>Status:</strong> User Created</p>
                </div>
              `);
              globalInfoWindow.setPosition(polygon.getPath().getAt(0));
              globalInfoWindow.open(newMap);
            });

            // When user clicks the polygon, also log the stored metadata (if any)
            polygon.addListener("click", () => {
              try {
                const meta = polygon.__meta;
                if (meta) {
                  console.log("Polygon clicked - metadata:", meta);
                } else {
                  // if no metadata attached, log coordinates as fallback
                  console.log(
                    "Polygon clicked - coordinates:",
                    getCoordsFromPath()
                  );
                }
              } catch (err) {
                console.error("Error retrieving polygon metadata:", err);
              }
            });

            // Reset drawing mode
            drawingManagerInstance.setDrawingMode(null);
            setIsDrawingMode(false);
          }
        );
      } else {
        console.warn(
          "Google Maps Drawing library not loaded. Drawing functionality will be disabled."
        );

        // Try to load the drawing library manually
        if (window.google && window.google.maps) {
          console.log("Attempting to load drawing library manually...");
          const script = document.createElement("script");
          script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=drawing&callback=initDrawingManager`;
          script.async = true;
          script.defer = true;

          window.initDrawingManager = () => {
            console.log("Drawing library loaded manually");
            // Re-initialize the map component
            window.location.reload();
          };

          document.head.appendChild(script);
        }
      }
    }
  }, [ref, map, center, zoom, userPolygons.length]);

  // Re-render API-provided polygons whenever the `polygons` prop changes.
  useEffect(() => {
    if (!map) return;

    // Clear any existing static polygons from the map
    (staticPolygonRef.current || []).forEach((p) => {
      try {
        globalInfoWindow.close(); // 👈 close when polygons are cleared
        p.setMap(null);
      } catch (e) {}
    });

    staticPolygonRef.current = [];

    if (!Array.isArray(polygons) || polygons.length === 0) {
      staticPolygonRef.current = [];
      return;
    }

    const newPolygonObjs = polygons
      .map((item, idx) => {
        if (!item || !Array.isArray(item.polygon) || item.polygon.length === 0)
          return null;
        const polygon = new window.google.maps.Polygon({
          paths: item.polygon,
          fillColor: "#22c55e",
          fillOpacity: 0.3,
          strokeColor: "#16a34a",
          strokeWeight: 2,
          clickable: true,
          editable: false,
          draggable: false,
        });
        polygon.setMap(map);
        polygon.id = item.id || item.property_no || idx;

        polygon.addListener("click", (e) => {
          const pos = e && e.latLng ? e.latLng : polygon.getPath().getAt(0);
          globalInfoWindow.setContent(`
            <div class="map-info-window">
              <h3>Property #${item.property_no || ""}</h3>
              <p><strong>Address:</strong> ${item.address_line || ""}, ${
            item.city || ""
          }</p>
              <p><strong>Status:</strong> ${item.status || ""}</p>
              <button onclick="deletePolygon(${
                polygon.id
              })" style="background: #dc2626; color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer; margin-top: 10px;">Delete</button>
            </div>
          `);
          globalInfoWindow.setPosition(pos);
          globalInfoWindow.open(map);
        });

        return polygon;
      })
      .filter(Boolean);

    staticPolygonRef.current = newPolygonObjs;

    // Fit map bounds to show all new polygons
    try {
      if (
        newPolygonObjs.length > 0 &&
        map &&
        window.google &&
        window.google.maps
      ) {
        const bounds = new window.google.maps.LatLngBounds();
        newPolygonObjs.forEach((poly) => {
          try {
            const path = poly.getPath ? poly.getPath() : null;
            if (path && path.getLength) {
              for (let i = 0; i < path.getLength(); i += 1) {
                bounds.extend(path.getAt(i));
              }
            }
          } catch (e) {
            // ignore
          }
        });
        // Only fit bounds if they are valid
        if (
          !bounds.isEmpty && typeof bounds.isEmpty === "function"
            ? !bounds.isEmpty()
            : true
        ) {
          map.fitBounds(bounds, 50);
        }
      }
    } catch (e) {
      // ignore
    }

    // Cleanup: remove polygons if props change or component unmounts
    return () => {
      newPolygonObjs.forEach((p) => {
        try {
          p.setMap(null);
        } catch (e) {
          // ignore
        }
      });
    };
  }, [polygons, map]);

  // Polygons from API are rendered above.

  // Function to toggle drawing mode
  const toggleDrawingMode = () => {
    if (
      drawingManager &&
      window.google.maps.drawing &&
      window.google.maps.drawing.DrawingManager
    ) {
      if (isDrawingMode) {
        drawingManager.setDrawingMode(null);
        setIsDrawingMode(false);
      } else {
        drawingManager.setDrawingMode(
          window.google.maps.drawing.OverlayType.POLYGON
        );
        setIsDrawingMode(true);
      }
    } else {
      alert(
        "Drawing functionality is not available. Please ensure the Google Maps Drawing library is loaded."
      );
    }
  };

  // Function to delete user polygon
  const deleteUserPolygon = async (polygonId) => {
    console.log("Deleting polygon with id:", polygonId);
    try {
      await api.deletePolygon(polygonId);

      toast.success("Polygon deleted successfully");

      // ✅ Always close the info window before removing polygon
      if (globalInfoWindow) {
        globalInfoWindow.close();
      }

      // Remove from userPolygons
      setUserPolygons((prev) => {
        const updated = prev.filter((p) => p.id !== polygonId);
        const polygonToDelete = prev.find((p) => p.id === polygonId);
        if (polygonToDelete) {
          polygonToDelete.polygon.setMap(null);
        }
        return updated;
      });

      // Remove from staticPolygonRef
      if (staticPolygonRef.current && staticPolygonRef.current.length > 0) {
        staticPolygonRef.current = staticPolygonRef.current.filter((poly) => {
          const polyId = String(
            poly.id ?? (poly.__meta && poly.__meta.id) ?? ""
          );
          if (polyId === String(polygonId)) {
            poly.setMap(null);
            return false;
          }
          return true;
        });
      }

      // Refresh polygons list from API
      if (
        typeof api.getProperties === "function" &&
        typeof setPolygons === "function"
      ) {
        try {
          const propertiesResp = await api.getProperties();
          const polygonsNormalized = (
            Array.isArray(propertiesResp.data)
              ? propertiesResp.data
              : propertiesResp || []
          )
            .map((p) => {
              const coords = p.polygon || p.polygon_coords || [];
              if (!Array.isArray(coords) || coords.length === 0) return null;
              return {
                ...p,
                polygon: coords.map((c) => ({
                  lat: Number(c.lat),
                  lng: Number(c.lng),
                })),
              };
            })
            .filter(Boolean);
          setPolygons(polygonsNormalized);
        } catch (err) {
          console.error("Failed to refresh polygons after delete", err);
        }
      }
    } catch (error) {
      toast.error("Failed to delete polygon");
      console.error("Delete polygon error:", error);
    }
  };

  // Make deletePolygon available globally for info window buttons
  useEffect(() => {
    window.deletePolygon = deleteUserPolygon;
    return () => {
      delete window.deletePolygon;
    };
  }, []);

  return (
    <div className="map-container">
      <div className="map-controls">
        <button
          className={`drawing-btn ${isDrawingMode ? "active" : ""}`}
          onClick={toggleDrawingMode}
          disabled={!drawingManager}
        >
          {isDrawingMode ? "Stop Drawing" : "Draw Polygon"}
        </button>
        <div className="drawing-instructions">
          {!drawingManager
            ? "Drawing library loading... Please wait."
            : isDrawingMode
            ? "Click on map to start drawing polygon. Double-click to finish."
            : 'Click "Draw Polygon" to create custom areas'}
        </div>
        {!drawingManager && (
          <div className="drawing-warning">
            <small>
              If drawing doesn't work, please check your Google Maps API key has
              Drawing library enabled.
            </small>
          </div>
        )}
      </div>
      <div ref={ref} className="google-map" />
    </div>
  );
};

const render = (status) => {
  switch (status) {
    case Status.LOADING:
      return (
        <div className="map-loading">
          <div className="loading-spinner"></div>
          <p>Loading map...</p>
        </div>
      );
    case Status.FAILURE:
      return (
        <div className="map-error">
          <p>Error loading map. Please check your API key.</p>
        </div>
      );
    default:
      return null;
  }
};

const GoogleMap = ({ polygons, setPolygons }) => {
  // Islamabad coordinates
  const center = { lat: 33.6844, lng: 73.0479 };
  const zoom = 12;

  return (
    <div className="google-map-container">
      <div className="map-header">
        <h3>Property Survey Map</h3>
        {/* <p>
          Colored property lines show individual houses and their survey status
          in Islamabad
        </p>
      </div>

      <div className="map-legend">
        <div className="legend-item">
          <div className="legend-color completed"></div>
          <span>Survey Completed</span>
        </div>
        <div className="legend-item">
          <div className="legend-color in-progress"></div>
          <span>Survey In Progress</span>
        </div>
        <div className="legend-item">
          <div className="legend-color pending"></div>
          <span>Survey Pending</span>
        </div>
        <div className="legend-item">
          <div className="legend-color custom"></div>
          <span>Custom Areas</span>
        </div> */}
      </div>

      <Wrapper
        apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={["drawing", "geometry"]}
        render={render}
      >
        <MapComponent
          center={center}
          zoom={zoom}
          polygons={polygons}
          setPolygons={setPolygons}
        />
      </Wrapper>
    </div>
  );
};

export default GoogleMap;
